import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { clearToken, retrieveToken, storeToken } from "../services/tokenVault.js";
import { setActiveProjectProviderId } from "../utils/activeProjectProvider.js";
import { resolveProjectProviderId } from "../utils/projectProvider.js";

const ProjectContext = createContext(null);

export function ProjectProvider({ projectId, children }) {
  const [project, setProject] = useState(null);
  const [_tokenReady, _setTokenReady] = useState(0);
  const [auditData, setAuditData] = useState(null);
  const [driftData, setDriftData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const apiToken = useMemo(() => {
    const orgUrl = project?.org?.url;
    if (!orgUrl) return null;
    return retrieveToken(orgUrl);
  }, [project?.org?.url, _tokenReady]);

  const setApiToken = useCallback((token, orgUrl) => {
    const url = orgUrl ?? project?.org?.url;
    if (!url) return;
    if (token == null || String(token).trim() === "") {
      clearToken(url);
    } else {
      storeToken(url, String(token).trim());
    }
    _setTokenReady((n) => n + 1);
  }, [project?.org?.url]);

  const disconnect = useCallback(() => {
    const orgUrl = project?.org?.url;
    if (orgUrl) clearToken(orgUrl);
    _setTokenReady((n) => n + 1);
  }, [project?.org?.url]);

  const loadProject = useCallback(async () => {
    if (!projectId) return;
    setIsLoading(true);
    try {
      const res = await fetch(`/api/projects/${encodeURIComponent(projectId)}`, {
        credentials: "include",
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data?.project) {
        const loadedProject = data.project;
        setProject(loadedProject);
        setActiveProjectProviderId(resolveProjectProviderId(loadedProject));
        const ou = loadedProject?.org?.url;
        if (ou && retrieveToken(ou)) {
          _setTokenReady((n) => n + 1);
        }
      } else {
        setProject(null);
        setActiveProjectProviderId(null);
      }
    } catch {
      setProject(null);
    } finally {
      setIsLoading(false);
    }
  }, [projectId]);

  const updateProjectLocally = useCallback((updates) => {
    setProject((prev) => (prev && updates ? { ...prev, ...updates } : prev));
  }, []);

  const syncProject = useCallback(
    async (updates) => {
      if (!projectId || !updates || typeof updates !== "object") return null;
      const res = await fetch(`/api/projects/${encodeURIComponent(projectId)}`, {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(updates),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data?.project) {
        setProject(data.project);
        return data.project;
      }
      return null;
    },
    [projectId],
  );

  const addRoadmapItem = useCallback(
    async (item) => {
      if (!projectId || !item?.id) return null;
      const cur = project?.roadmap?.items;
      const items = Array.isArray(cur) ? [...cur, item] : [item];
      return syncProject({ roadmap: { ...project?.roadmap, items } });
    },
    [projectId, project, syncProject],
  );

  const updateRoadmapItem = useCallback(
    async (itemId, updates) => {
      if (!projectId) return null;
      if (typeof itemId !== "string" || !itemId.trim()) {
        // eslint-disable-next-line no-console
        console.warn("[ProjectContext] Invalid roadmap itemId:", itemId);
        return null;
      }
      const url = `/api/projects/${encodeURIComponent(projectId)}/roadmap/items/${encodeURIComponent(itemId)}`;
      // eslint-disable-next-line no-console -- triage client/server roadmap path mismatches
      console.log("[updateRoadmapItem]", "PATCH", url);
      try {
        const res = await fetch(url, {
          method: "PATCH",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(updates || {}),
        });
        const text = await res.text();
        let data = {};
        try {
          data = text ? JSON.parse(text) : {};
        } catch {
          // eslint-disable-next-line no-console
          console.error("[updateRoadmapItem] Non-JSON response:", res.status, text?.slice(0, 500));
          return null;
        }
        if (!res.ok) {
          // eslint-disable-next-line no-console
          console.error("[updateRoadmapItem] failed:", res.status, data?.error || text?.slice(0, 300));
          return null;
        }
        if (data.ok && data.project) {
          setProject(data.project);
          return data.project;
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("[updateRoadmapItem] error:", err);
      }
      return null;
    },
    [projectId],
  );

  const appendLog = useCallback(
    async (logEntry) => {
      if (!projectId) return null;
      const res = await fetch(
        `/api/projects/${encodeURIComponent(projectId)}/execution-log`,
        {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(logEntry || {}),
        },
      );
      const data = await res.json().catch(() => ({}));
      if (res.ok && data?.project) {
        setProject(data.project);
        return data.project;
      }
      return null;
    },
    [projectId],
  );

  useEffect(() => {
    loadProject();
  }, [loadProject]);

  const value = useMemo(
    () => ({
      projectId,
      project,
      apiToken,
      auditData,
      driftData,
      isLoading,
      setAuditData,
      setDriftData,
      loadProject,
      setApiToken,
      disconnect,
      updateProjectLocally,
      syncProject,
      addRoadmapItem,
      updateRoadmapItem,
      appendLog,
      setProject,
    }),
    [
      projectId,
      project,
      apiToken,
      auditData,
      driftData,
      isLoading,
      setAuditData,
      setDriftData,
      loadProject,
      setApiToken,
      disconnect,
      updateProjectLocally,
      syncProject,
      addRoadmapItem,
      updateRoadmapItem,
      appendLog,
      setProject,
    ],
  );

  return <ProjectContext.Provider value={value}>{children}</ProjectContext.Provider>;
}

export function useProject() {
  const ctx = useContext(ProjectContext);
  if (!ctx) throw new Error("useProject must be used within ProjectProvider");
  return ctx;
}

/** Same store as useProject, or null when outside ProjectProvider (e.g. Dashboard fallbacks). */
export function useProjectOptional() {
  return useContext(ProjectContext);
}
